import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Upload, X, File } from 'lucide-react'
import { toast } from 'sonner'

interface NodeFileProps {
  uploadedFile?: File | null
  onFileChange?: (file: File | null) => void
  loading?: boolean
  disabled?: boolean
  label?: string
  description?: string
  supportedFormats?: string
}

export function NodeFile({
  uploadedFile,
  onFileChange,
  loading = false,
  disabled = false,
  label = 'Upload File',
  description = 'Drag and drop a file here, or click to select',
  supportedFormats = 'Supported formats: PDF, images, documents, spreadsheets, presentations',
}: NodeFileProps) {
  // File upload logic
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0 && onFileChange) {
        const file = acceptedFiles[0]
        onFileChange(file)
        toast.success(`File "${file.name}" ready to upload`)
      }
    },
    [onFileChange]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    disabled: disabled || loading,
  })

  const removeFile = () => {
    if (onFileChange) {
      onFileChange(null)
    }
  }

  if (!onFileChange) return null

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {!uploadedFile ? (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50'
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          {isDragActive ? (
            <p className="text-sm text-muted-foreground">Drop the file here...</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-2">{description}</p>
              <p className="text-xs text-muted-foreground">{supportedFormats}</p>
            </>
          )}
        </div>
      ) : (
        <div className="border rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <File className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{uploadedFile.name}</p>
              <p className="text-xs text-muted-foreground">
                {(uploadedFile.size / 1024).toFixed(2)} KB
              </p>
            </div>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={removeFile} disabled={loading}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
